import React from 'react';
import { SxProps, TextField, Theme } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import enGB from 'date-fns/locale/en-GB';
import {
  Controller,
  FieldValues,
  RegisterOptions,
  useFormContext,
} from 'react-hook-form';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import {
  BorderRadius,
  ColorPalette,
  TypographyFamily,
} from '../../styles/constants';
import { isValid as isValidDate } from 'date-fns';

interface Props {
  name: string;
  label?: string;
  defaultValue?: Date;
  isDisabled?: boolean;
  sx?: SxProps<Theme>;
  inputFormat?: InputFormat;
  openTo?: 'day' | 'year' | 'month';
  rules?: Omit<
    RegisterOptions<FieldValues, string>,
    'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'
  >;
  maxDate?: Date;
  minDate?: Date;
}

export enum InputFormat {
  SEPARATOR_SPACE = 'dd MMM yyyy',
  SEPARATOR_FORWARD_SLASH = 'dd/MM/yyyy',
  YEAR = 'yyyy',
}

const popperSx: SxProps = {
  '&': {
    fontFamily: TypographyFamily.ARIAL,
  },

  '& .Mui-selected': {
    borderRadius: BorderRadius.SMALL,
    background: ColorPalette.MIDDLE_BLUE,
  },
  '& .MuiButtonBase-root': {
    fontFamily: TypographyFamily.ARIAL,
    fontSize: '16px',
  },
  '& .MuiPickersDay-today': {
    borderRadius: BorderRadius.SMALL,
  },

  //style for day header
  '&  [role="columnheader"]': {
    fontWeight: 700,
    fontFamily: TypographyFamily.ARIAL,
    color: ColorPalette.RAISIN_BLACK,
    fontSize: '16px',
  },

  // hairline style under the day header
  '& .MuiCalendarPicker-viewTransitionContainer > div > div > [role="row"]': {
    borderBottom: '1px #DADADD solid',
  },

  //style for day header weekend
  '& [role="columnheader"]:nth-last-of-type(1), & [role="columnheader"]:nth-last-of-type(2)':
    {
      color: ColorPalette.VERDIGRIS,
    },
  //style for weekend number for selected month
  '& [role="gridcell"]:nth-last-of-type(6n + 2):not(.MuiPickersDay-dayOutsideMonth), & [role="gridcell"]:nth-last-of-type(7n + 1):not(.MuiPickersDay-dayOutsideMonth)':
    {
      color: ColorPalette.VERDIGRIS,
      fontWeight: 400,
    },
  //style for weekend number outside the month
  '& .MuiPickersDay-dayOutsideMonth:nth-last-of-type(6n + 2), & .MuiPickersDay-dayOutsideMonth:nth-last-of-type(7n + 1)':
    {
      color: ColorPalette.VERDIGRIS,
      opacity: 0.6,
    },

  //style for month
  '& .css-1v994a0': {
    fontWeight: 700,
    fontSize: '18px',
    color: ColorPalette.RAISIN_BLACK,
  },

  //style for arrow icons (svg)
  '& svg': {
    color: ColorPalette.DARK_GRAY,
    fontSize: '30px',
  },
  '& .MuiPickersArrowSwitcher-spacer': {
    width: 0,
  },

  //style for years and month buttons
  '& .PrivatePickersYear-yearButton, & .PrivatePickersMonth-root': {
    fontFamily: TypographyFamily.ARIAL,
    fontSize: '16px',
    color: ColorPalette.RAISIN_BLACK,
    fontWeight: 400,
  },
};

export function AppControlledCalendar({
  sx: customStyles,
  name,
  label,
  defaultValue,
  isDisabled = false,
  inputFormat = InputFormat.SEPARATOR_FORWARD_SLASH,
  openTo = 'day',
  rules,
  maxDate,
  minDate,
}: Props): JSX.Element {
  const { control } = useFormContext();

  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue}
      rules={{
        validate: {
          isValid: (value: Date) => {
            return isValidDate(value) || 'Invalid date';
          },
          ...rules?.validate,
        },
      }}
      render={({ field, fieldState }) => (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
          <DatePicker
            disabled={isDisabled}
            dayOfWeekFormatter={(nameOfDay) => nameOfDay.substring(0, 2)}
            inputFormat={inputFormat}
            label={label}
            disableMaskedInput
            openTo={openTo}
            views={['year', 'month', 'day']}
            value={field.value}
            minDate={minDate}
            maxDate={maxDate}
            onChange={field.onChange}
            renderInput={(params) => (
              <TextField
                sx={{ ...customStyles }}
                {...params}
                id={name}
                variant="outlined"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
            showDaysOutsideCurrentMonth
            components={{
              OpenPickerIcon: label
                ? CalendarMonthOutlinedIcon
                : KeyboardArrowDownIcon,
              SwitchViewButton: KeyboardArrowDownIcon,
            }}
            PopperProps={{
              sx: popperSx,
            }}
          />
        </LocalizationProvider>
      )}
    />
  );
}
